import type { Item } from '@erp/shared';
import {
  Barcode,
  Box,
  FolderTree,
  Package,
  PackagePlus,
  Plus,
  Star,
  Tag,
  Wrench,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Separator,
} from '../../components/shadcn/index.js';
import { Button, FitImage } from '../../components/ui/index.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatNumber } from '../../lib/intl.js';
import { formatMoney, trimMoney } from '../../lib/money.js';
import { isHtmlEmpty, sanitizeHtml } from '../../lib/sanitizeHtml.js';

import { CollapsibleHtml } from './CollapsibleHtml.js';
import { itemGradient, useEntityImages, useItemImage } from './itemImage.js';

/**
 * Small square product thumbnail: the item's first image attachment (auth-fetched
 * object URL) with a deterministic gradient + icon fallback. Reusable wherever a
 * compact product image is needed (POS cart lines, lists, pickers).
 */
export function ItemThumb({
  itemId,
  seed,
  enabled,
  className,
  isService,
}: {
  itemId: string | null | undefined;
  seed: string;
  enabled: boolean;
  className?: string;
  isService?: boolean;
}): JSX.Element {
  const url = useItemImage(itemId, enabled);
  const Icon = isService ? Wrench : Package;
  return (
    <span
      className={'relative grid shrink-0 place-items-center overflow-hidden rounded-md text-white/90 ' + (className ?? 'h-10 w-10')}
      style={url ? undefined : { backgroundImage: itemGradient(seed || 'x') }}
    >
      {url ? (
        // Fitted whole (never cropped or zoomed), white/black bars where it doesn't fill.
        <FitImage src={url} />
      ) : (
        <Icon className="h-1/2 w-1/2 drop-shadow" aria-hidden />
      )}
    </span>
  );
}

export interface ItemDetailDialogProps {
  item: Item | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locale: string;
  /**
   * Currency used when the item has no own sale currency. Defaults to the
   * tenant's BASE currency — which is what the server assumes for such items —
   * so a store selling in IQD never renders a price as "$2,500".
   */
  currencyFallback?: string;
  /** Gate image fetching to users who can read attachments. */
  canViewImages?: boolean;
  categoryName?: string | null;
  brandName?: string | null;
  /**
   * The attached customer's GROUP (price-list) prices for this item, in the
   * group's own currency — what this customer is actually charged. When set they
   * REPLACE the catalogue price (which is still shown as the list price, since
   * it may be denominated in another currency and is never converted).
   */
  groupPrices?: {
    currencyCode: string;
    groupName?: string | null;
    /** Base-unit price; null ⇒ the group has no price for the base unit. */
    base: string | null;
    /** itemUnitId → price; missing/null ⇒ no group price for that unit. */
    units?: Record<string, string | null>;
  } | null;
  /**
   * When provided, the modal shows "Add to cart" actions (base unit + one per
   * packaging unit). Omit on read-only pages (e.g. the items catalogue).
   */
  onAddToCart?: (item: Item, unitId: string | null) => void | Promise<void>;
  busy?: boolean;
}

/**
 * Rich, reusable product-details modal. Shows an image gallery, pricing (incl.
 * packaging units), and metadata; optionally offers add-to-cart actions. Used by
 * the POS register and can be dropped onto the items page or any product list.
 */
export function ItemDetailDialog({
  item,
  open,
  onOpenChange,
  locale,
  currencyFallback,
  canViewImages = false,
  categoryName,
  brandName,
  groupPrices,
  onAddToCart,
  busy,
}: ItemDetailDialogProps): JSX.Element {
  const { t } = useTranslation('items');
  // Cleaned once per description rather than on every render of a dialog that
  // re-renders on image, price and quantity changes.
  const cleanDesc = useMemo(() => sanitizeHtml(item?.description ?? ''), [item?.description]);
  const tenantBaseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode);
  const images = useEntityImages('item', open ? item?.id : null, canViewImages);
  const [active, setActive] = useState(0);
  useEffect(() => { setActive(0); }, [item?.id]);

  // An item with no own currency is priced in the tenant BASE currency (the
  // server's own assumption) — never a hardcoded 'USD'.
  const currency = item?.saleCurrencyCode ?? currencyFallback ?? tenantBaseCurrency ?? '';
  const money = (v: string | null | undefined): string =>
    formatMoney(v ?? '0', { locale, currency });
  /** The customer's group price for a unit (`null` = the base unit), if any. */
  const groupPriceFor = (unitId: string | null): string | null => {
    if (!groupPrices) return null;
    return (unitId ? groupPrices.units?.[unitId] ?? null : groupPrices.base) ?? null;
  };
  const groupMoney = (v: string): string =>
    formatMoney(v, { locale, currency: groupPrices?.currencyCode ?? currency });
  const basePrice = groupPriceFor(null);
  /**
   * Whether to show stock at all.
   *
   * NOT gated on `isInventoryTracked` alone: a quarter of this catalogue is
   * untracked and 2,000-odd of those still carry a real quantity (a stocktake
   * import writes the number whatever the flag says), and hiding it left the
   * cashier staring at a card that knew the answer and wouldn't say it. So:
   * a tracked item always shows — 0 included, that IS the answer — and an
   * untracked one shows only when it actually holds something, which keeps
   * services and never-counted goods from sprouting a meaningless "0".
   */
  const showStock =
    !!item && item.onHand !== undefined && (item.isInventoryTracked || Number(item.onHand) !== 0);
  const Icon = item?.itemType === 'service' ? Wrench : Package;
  const mainUrl = images[active] ?? images[0] ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pe-6">
            <span className="truncate">{item?.name ?? ''}</span>
            {item?.isFeatured && (
              <Star className="h-4 w-4 shrink-0 fill-warning text-warning" aria-hidden />
            )}
          </DialogTitle>
        </DialogHeader>

        {item && (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {/* ---- gallery ---- */}
            <div className="flex flex-col gap-2">
              <div
                className={
                  'relative grid aspect-square w-full place-items-center overflow-hidden rounded-xl text-white/90 ring-1 ring-border'
                }
                style={mainUrl ? undefined : { backgroundImage: itemGradient(item.id) }}
              >
                {mainUrl ? (
                  // Fitted whole, never cropped or zoomed; white/black bars where it doesn't fill.
                  <FitImage src={mainUrl} alt={item.name} />
                ) : (
                  <Icon className="h-20 w-20 drop-shadow" aria-hidden />
                )}
              </div>
              {images.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  {images.map((u, i) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setActive(i)}
                      className={
                        'relative h-14 w-14 overflow-hidden rounded-md ring-2 transition ' +
                        (i === active ? 'ring-brand' : 'ring-transparent hover:ring-border-strong')
                      }
                    >
                      <FitImage src={u} />
                    </button>
                  ))}
                </div>
              )}

              {/* Description, as the rich text it IS. The field is written in an
                  editor that stores HTML, so printing it as plain text showed
                  the cashier the tags — `<p>`, `<ul>`, `<li>` — instead of the
                  formatting. Sanitized on the way out as well as on the way in:
                  a description is staff-authored, but so is anything that gets
                  pasted into an editor from a supplier's site.

                  It sits under the PHOTO rather than under the details, which is
                  also where the room is: the details column carries the price,
                  the identifiers, the per-warehouse stock and the packaging
                  units, while this one ended at the thumbnails. */}
              {!isHtmlEmpty(cleanDesc) && (
                <div>
                  <Separator className="mb-2" />
                  <CollapsibleHtml
                    html={cleanDesc}
                    moreLabel={t('detail.showMore', { defaultValue: 'Show more' })}
                    lessLabel={t('detail.showLess', { defaultValue: 'Show less' })}
                  />
                </div>
              )}
            </div>

            {/* ---- details ---- */}
            <div className="flex min-w-0 flex-col gap-3">
              {/* price — the customer's group price wins over the catalogue one */}
              <div className="rounded-xl border border-border bg-bg-subtle/40 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-fg-muted">
                  {basePrice != null
                    ? t('detail.customerPrice', { defaultValue: 'Customer price' })
                    : t('detail.salePrice', { defaultValue: 'Sale price' })}
                </div>
                <div className="font-mono text-2xl font-extrabold tabular-nums text-fg">
                  {basePrice != null ? groupMoney(basePrice) : money(item.salePrice)}
                </div>
                {basePrice != null && (
                  <div className="mt-0.5 text-xs text-fg-muted">
                    {groupPrices?.groupName ? `${groupPrices.groupName} · ` : ''}
                    {t('detail.listPrice', {
                      price: money(item.salePrice),
                      defaultValue: 'List price {{price}}',
                    })}
                  </div>
                )}
              </div>

              {/* identifiers */}
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
                <dt className="text-fg-muted">{t('detail.code', { defaultValue: 'Code / SKU' })}</dt>
                <dd className="truncate font-mono text-fg">{item.code}</dd>
                {item.barcode && (
                  <>
                    <dt className="flex items-center gap-1 text-fg-muted">
                      <Barcode className="h-3.5 w-3.5" aria-hidden />
                      {t('detail.barcode', { defaultValue: 'Barcode' })}
                    </dt>
                    <dd className="truncate font-mono text-fg">{item.barcode}</dd>
                  </>
                )}
                <dt className="text-fg-muted">{t('detail.unitOfMeasure', { defaultValue: 'Unit' })}</dt>
                <dd className="text-fg">{item.unitOfMeasure}</dd>
                {/* Shown only when the CALLER already carries the number (the
                    register loads it with the catalogue, the items list behind
                    its Quantity toggle) — this dialog never fetches stock of its
                    own, so it can't hand a cashier a figure nobody asked for. */}
                {showStock && (
                  <>
                    <dt className="text-fg-muted">{t('detail.onHand', { defaultValue: 'In stock' })}</dt>
                    <dd className={'font-mono tabular-nums ' + (Number(item.onHand) > 0 ? 'text-fg' : 'text-danger')}>
                      {formatNumber(Number(item.onHand), { locale })} {item.unitOfMeasure}
                    </dd>
                  </>
                )}
              </dl>

              {/* Where that stock actually IS. A shop whose tills are pinned to
                  different warehouses can hold plenty of something the register
                  in front of you cannot sell, and a single total hides exactly
                  that. Every active warehouse is listed, including the empty
                  ones — a missing row and a zero look identical otherwise. */}
              {showStock && item.onHandByWarehouse && item.onHandByWarehouse.length > 0 && (
                <div className="rounded-md border border-border">
                  <p className="border-b border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                    {t('detail.byWarehouse', { defaultValue: 'By warehouse' })}
                  </p>
                  <ul className="divide-y divide-border/60">
                    {item.onHandByWarehouse.map((w) => {
                      const n = Number(w.quantity);
                      return (
                        <li key={w.warehouseId} className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
                          <span className="min-w-0 truncate text-fg-muted">{w.name}</span>
                          <span className={'font-mono tabular-nums ' + (n > 0 ? 'font-semibold text-fg' : n < 0 ? 'text-danger' : 'text-fg-subtle')}>
                            {formatNumber(n, { locale })}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* meta chips */}
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="secondary">
                  {item.itemType === 'service'
                    ? t('detail.service', { defaultValue: 'Service' })
                    : t('detail.product', { defaultValue: 'Product' })}
                </Badge>
                {categoryName && (
                  <Badge variant="secondary" className="gap-1">
                    <FolderTree className="h-3 w-3" aria-hidden />
                    {categoryName}
                  </Badge>
                )}
                {brandName && (
                  <Badge variant="secondary" className="gap-1">
                    <Tag className="h-3 w-3" aria-hidden />
                    {brandName}
                  </Badge>
                )}
                {item.isInventoryTracked && (
                  <Badge variant="secondary" className="gap-1">
                    <Box className="h-3 w-3" aria-hidden />
                    {t('detail.tracked', { defaultValue: 'Stock-tracked' })}
                  </Badge>
                )}
                {!item.isActive && (
                  <Badge variant="warning">{t('detail.inactive', { defaultValue: 'Inactive' })}</Badge>
                )}
              </div>

              {/* packaging units */}
              {item.units.length > 0 && (
                <div>
                  <div className="mb-1 text-xs uppercase tracking-wide text-fg-muted">
                    {t('detail.units', { defaultValue: 'Units' })}
                  </div>
                  <ul className="flex flex-col gap-1">
                    <li className="flex items-center justify-between rounded-md border border-border-subtle bg-bg px-2.5 py-1.5 text-sm">
                      <span className="flex items-center gap-1.5">
                        <Box className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                        {t('detail.baseUnit', { defaultValue: 'Single' })} · {item.unitOfMeasure}
                      </span>
                      <span className="font-mono tabular-nums">
                        {basePrice != null ? groupMoney(basePrice) : money(item.salePrice)}
                      </span>
                    </li>
                    {item.units.map((u) => {
                      const g = groupPriceFor(u.id);
                      return (
                        <li
                          key={u.id}
                          className="flex items-center justify-between rounded-md border border-border-subtle bg-bg px-2.5 py-1.5 text-sm"
                        >
                          <span className="flex items-center gap-1.5">
                            <PackagePlus className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                            {u.name}
                            <span className="text-xs text-fg-muted">· ×{trimMoney(u.factor)}</span>
                          </span>
                          <span className="font-mono tabular-nums">
                            {g != null ? groupMoney(g) : money(u.salePrice ?? item.salePrice)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---- add-to-cart actions ---- */}
        {item && onAddToCart && item.isSellable && (
          <>
            <Separator />
            <div className="flex flex-wrap gap-2">
              <Button
                className="flex-1"
                disabled={busy}
                onClick={() => void onAddToCart(item, null)}
              >
                <Plus className="h-4 w-4" aria-hidden />
                {item.units.length > 0
                  ? `${t('detail.add', { defaultValue: 'Add' })} · ${t('detail.baseUnit', { defaultValue: 'Single' })}`
                  : t('detail.addToCart', { defaultValue: 'Add to cart' })}
              </Button>
              {item.units.map((u) => (
                <Button
                  key={u.id}
                  variant="secondary"
                  disabled={busy}
                  onClick={() => void onAddToCart(item, u.id)}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  {u.name}
                </Button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
